import styled, { css } from 'styled-components';
import { Typography } from '@/components/UI/Typography/Typography.styles';
import { ellipsisMultiline } from '@/styles/helpers';

export const BreadcrumbsContainer = styled.div`
  background-color: ${({ theme }) => theme.palette.primary.background};
  padding: 0 30px;

  @media screen and (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: 0 15px;
  }
`;

export const Breadcrumbs = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 16px 0;
  display: flex;
  align-items: center;
  font-size: 15px;
  font-weight: 400;
  color: ${({ theme }) => theme.palette.primary.labels};

  a {
    color: ${({ theme }) => theme.palette.primary.labels};
    text-decoration: none;

    &:hover {
      color: ${({ theme }) => theme.palette.brand.main};
    }
  }
`;

export const BreadcrumbsShopName = styled.span`
  font-size: 15px;
  color: ${({ theme }) => theme.palette.primary.labels};

  &::before {
    content: '•';
    margin: 0 10px;
    color: ${({ theme }) => theme.palette.secondary.labels};
  }
`;

export const CoverBackground = styled.div<{ $bg: string }>`
  position: absolute;
  inset: 0;
  background: ${({ $bg }) => `url(${$bg}) center/cover no-repeat`};
  filter: blur(5px);
  background-color: rgba(0, 0, 0, 0.01);
  z-index: 1;
`;

export const CoverWrapper = styled.div`
  width: 100%;
  overflow: hidden;
  position: relative;
  min-height: auto;

  @media (min-width: 950px) {
    min-height: 266px;
  }
`;

export const CoverImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  position: relative;
  z-index: 2;

  @media (min-width: 1100px) {
    max-height: 300px;
    width: auto;
    margin: 0 auto;
  }
`;

export const ContentTop = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  background-color: ${({ theme }) => theme.palette.primary.background};
  padding-bottom: 35px;
  border-radius: 0 0 40px 40px;
  overflow: visible;
  position: relative;

  @media (max-width: 744px) {
    padding: 0 0 20px;
    border-radius: 0 0 21px 21px;
  }
`;

export const ContentBottom = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  background-color: ${({ theme }) => theme.palette.primary.background};
  padding: 40px 40px 60px;
  margin-top: 20px;
  border-radius: 40px 40px 0 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: 15px 0;
    border-radius: 21px 21px 0 0;
    min-height: auto;
  }
`;

export const ContentContainer = styled.div`
  background-color: ${({ theme }) => theme.palette.secondary.background};
`;

export const ContentOffers = styled.div`
  color: ${({ theme }) => theme.palette.primary.labels};
  font-size: 20px;
  font-weight: 500;
  margin-bottom: 32px;

  @media (max-width: 949px) {
    margin-bottom: 25px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: 0 15px;
    margin-bottom: 20px;
  }
`;

export const TopSection = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 20px 40px 35px;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex-direction: column;
    padding: 15px;
    gap: 16px;
  }
`;

export const LeftTop = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 20px;
  flex-wrap: wrap;
  position: relative;
  padding-left: 145px;

  @media (max-width: 949px) {
    position: relative;
    padding-left: 0;
    flex-direction: row;
    align-items: flex-start;
  }
`;

export const ShopInfoBox = styled.div`
  display: flex;
  align-items: center;
`;

export const LogoWrapper = styled.div<{ $bgImage: string }>`
  width: 130px;
  height: 130px;
  border-radius: 21px;
  border: 2px solid ${({ theme }) => theme.palette.primary.border};
  overflow: hidden;
  background-color: ${({ theme }) => theme.palette.primary.background};
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  left: 0;
  bottom: 0;
  background-image: ${({ $bgImage }) => `url(${$bgImage})`};
  background-size: cover;
  background-repeat: no-repeat;
  z-index: 2;

  @media screen and (max-width: ${({ theme }) => theme.breakpoints.md}) {
    position: relative;
    bottom: 30px;
    left: 0;
    width: 80px;
    height: 80px;
  }
`;

export const LogoImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

export const ShopTexts = styled.div`
  display: flex;
  flex-direction: column;
`;

export const ShopName = styled.h1`
  font-size: 20px;
  font-weight: 500;
  color: ${({ theme }) => theme.palette.primary.labels};
  margin: 0;
`;

export const ShopWorkTime = styled.div`
  font-size: 15px;
  color: ${({ theme }) => theme.palette.secondary.labels};
  margin-top: 8px;

  @media screen and (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 14px;
  }
`;

export const ShopStats = styled.div`
  font-size: 15px;
  color: ${({ theme }) => theme.palette.primary.labels};
  margin-top: 8px;

  @media screen and (max-width: ${({ theme }) => theme.breakpoints.md}) {
    position: absolute;
    bottom: -10px;
    left: 0;
    font-size: 14px;
  }
`;

export const ShopDescription = styled(Typography.Body)<{ $isOpen?: boolean }>`
  ${({ $isOpen }) => !$isOpen && ellipsisMultiline(2)}
  font-size: 15px;
  font-weight: 400;
  color: ${({ theme }) => theme.palette.primary.labels};
  margin-top: 12px;
  width: 65%;

  @media screen and (max-width: ${({ theme }) => theme.breakpoints.md}) {
    width: 100%;
    margin-top: 20px;
  }
`;

export const ShopTextGroup = styled.div`
  display: flex;
  flex-direction: column;

  @media screen and (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex: 1;
  }
`;

export const ShopExtraInfo = styled.div`
  padding: 0 40px;

  @media screen and (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 5px 40px 20px;
  }

  @media screen and (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: 15px;
  }
`;

export const ShopPromo = styled.div`
  color: ${({ theme }) => theme.palette.primary.labels};
  font-size: 16px;
  font-weight: 500;
  margin: 0 0 15px;
`;

export const Toggle = styled(Typography.Body)`
  color: ${({ theme }) => theme.palette.system.blue};
  text-decoration: underline;
  max-width: max-content;
  margin-top: 10px;
  cursor: pointer;
`;

export const ShopAddress = styled.p`
  font-size: 15px;
  font-weight: 400;
  color: ${({ theme }) => theme.palette.primary.labels};
  text-decoration: underline;
  margin-top: 29px;
  background-image: url('/images/shops/location.svg');
  background-repeat: no-repeat;
  background-position: left center;
  padding-left: 20px;
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.palette.brand.main};
  }
`;

export const ShopRightContainer = styled.div`
  position: absolute;
  top: 20px;
  right: 40px;
`;
export const ContactBox = styled.div`
  flex-shrink: 0;
  z-index: 5;

  @media screen and (max-width: ${({ theme }) => theme.breakpoints.md}) {
    position: fixed;
    right: 15px;
    left: 0;
    bottom: ${({ theme }) =>
      theme.isWebView ? `${+theme.insetsBottom + 15}px` : '15px'};
    margin: 0 auto;
    padding: 0 15px;
    width: 100%;
    display: flex;
    gap: 10px;
    justify-content: space-between;
  }
`;

export const FloatingButton = styled.button`
  padding: 16px 25px;
  width: 260px;
  border: none;
  border-radius: 14px;
  background: ${({ theme }) => theme.palette.system.green};
  color: ${({ theme }) => theme.palette.primary.background};
  font-size: 15px;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;

  svg {
    margin-right: 10px;
  }

  &:hover {
    background: ${({ theme }) => theme.palette.system.deepGreen};
  }

  @media screen and (max-width: ${({ theme }) => theme.breakpoints.md}) {
    width: 100%;
    padding: 17px;
    justify-content: center;
  }
`;

export const PhoneText = styled.span`
  @media screen and (max-width: ${({ theme }) => theme.breakpoints.md}) {
    margin-left: 10px;
  }
`;

export const PhoneList = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid ${({ theme }) => theme.palette.system.green};
  border-radius: 14px;
  padding: 16px 50px 16px 80px;
  gap: 12px;
  align-items: center;
  background: ${({ theme }) => theme.palette.primary.background};
  font-size: 18px;
  font-weight: 500;
  color: ${({ theme }) => theme.palette.primary.labels};
  background-image: url('/images/shops/phone.svg');
  background-repeat: no-repeat;
  background-position: 50px 18px;

  a:hover {
    color: ${({ theme }) => theme.palette.brand.main};
  }
`;

export const Photo = styled.img`
  width: calc(20% - 8px);
  height: 120px;
  object-fit: cover;
  border-radius: 8px;
`;

export const ShopAdsEmpty = styled.div`
  height: 500px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.palette.secondary.labels};
  text-align: center;
  font-size: 16px;
  font-weight: 400;
`;

export const ShopFavorite = styled.button<{ $isFavoritedLocal: boolean }>`
  padding: 17px 39px;
  border-radius: 14px;
  color: ${({ theme }) => theme.palette.system.blue};
  background: ${({ theme }) => theme.palette.system.lightBlue};
  display: flex;
  justify-content: center;
  align-items: center;
  width: 260px;
  border: 0;
  margin-bottom: 10px;

  @media screen and (max-width: ${({ theme }) => theme.breakpoints.md}) {
    margin-bottom: 0;
    width: 100%;
    padding: 17px;
  }

  ${({ $isFavoritedLocal }) =>
    $isFavoritedLocal &&
    css`
      color: ${({ theme }) => theme.palette.primary.labels};
      background: ${({ theme }) => theme.palette.secondary.background};
    `}
`;
